
/**
 * This page is a generic page automatically generated from Strapi's datas with the page collection.
 *
 * URL: /:language(fr|en|es)/:urlParams_1/:urlParams_2/:urlParams_3
 *
 * @module /genericPage
 */

import { gql } from '@apollo/client';
import { Publication } from '@bruce.work/bruce-doc-class-typescript/doc/Class/Publication';
import { Recorded } from '@bruce.work/bruce-doc-event-typescript/doc/Class';
import { PageBasicParams, PageBasicProps } from 'interfaces';
import Head from 'next/head';
import { customReactElementPerPage, sectionsToInsertPerPage } from './config';
import { insertSection, renderComponents } from './helper';
import { PageGql } from './query';
import { INJECTABLE_SECTIONS, Page } from './type';
import { queryForPublications } from '@/components/sections/SectionWithMissionCard';
import PageWrapper from '@/components/wrappers/PageWrapper';
import { startServerSide } from '@/parse/server';
import { renderScript } from '@/utilities/functions';
import { apolloClient } from '@/utilities/query/apollo';

// #region COMPONENT
const GenericPage = ({ body, currentPath, currentLang, page, publications }: PageBasicProps & {
	body: Page['body'];
	page: Page;
	publications?: Recorded<Publication>[];
}) => {

	// #region RENDER
	return (
		<PageWrapper
			alternateUrl={
				page.seo.alternateURL?.reduce(
					(reduction, alternateUrl) => ({
						...reduction,
						[alternateUrl.locale.toLowerCase()]: alternateUrl.url,
					}),
					{},
				)
			}
			currentLang={currentLang}
			currentPath={currentPath}
			description={page.seo.metaDescription}
			introImageUrl={page.seo.metaImage.data.attributes.url}
			isv2
			pageType={page.pageType}
			title={page.seo.metaTitle}
			footerData={page.footer.data.attributes}
			navbarData={page.navbar.data.attributes}
		>
			<Head> {renderScript(page.seo.structuredData)} </Head>
			<>
				{body.map(
					(component, index) => (
						renderComponents({
							body: component.__typename === INJECTABLE_SECTIONS.SUMMARY ? body : undefined,
							component,
							customReactElementPerPage: customReactElementPerPage[page.name],
							index,
							publications,
							currentLang,
						})
					),
				)}
			</>
		</PageWrapper>
	);
	// #endregion
};
// #endregion

// #region CONNEXION
export const getServerSideProps = async ({ res, query }: { res: any; query: PageBasicParams }) => {

	res.setHeader('Cache-Control', 's-maxage=600, stale-while-revalidate=900');

	// #region CONSTANT
	const pageQuery = `
		pages(
			locale: "${query.language}",
			filters: { pagePath: { eq: "${query.pagePath}" }},
			publicationState: LIVE
		) {
			data {
				attributes {
					${PageGql}
				}
			}
		}
	`;
	// #endregion

	// #region FETCH
	try {
		const { data, errors } = await apolloClient.query({
			query: gql`
				query {
					${pageQuery}
				}
			`,
		});

		const page: Page = data.pages.data[0].attributes;

		if (errors) {
			return { notFound: true };
		}
		if (!page) {
			return { notFound: true };
		}
		if (page) {

			if (page.pageFullPath === query.requestedURL) {

				const body: Page['body'] =
					page.enableSummary || sectionsToInsertPerPage[page.name] || sectionsToInsertPerPage[page.name.split('_')[0]]
						? insertSection({
							sectionsToInsert: sectionsToInsertPerPage[page.name] ?? sectionsToInsertPerPage[page.name.split('_')[0]],
							summaryEnabled: page.enableSummary,
							targetArray: page.body,
						})
						: page.body;

				const needsPublications = () => {
					const hasPublicationComponent = page.body.some(component =>
						component.__typename === 'ComponentSectionWithMissionCard',
					);
					const injectedSections = sectionsToInsertPerPage[page.name] ?? sectionsToInsertPerPage[page.name.split('_')[0]];
					const hasInjectedPublicationComponent = injectedSections && Object.values(injectedSections).some(
						(section: any) => section.__typename === INJECTABLE_SECTIONS.MISSION_CARDS_PREVIEW,
					);

					return hasPublicationComponent || hasInjectedPublicationComponent;
				};

				// Fetch publications data only if needed
				let publications: Publication[] | undefined;
				if (needsPublications()) {
					try {
						publications = await startServerSide.query(queryForPublications());
					} catch (publicationError) {
						console.warn('Could not fetch publications:', publicationError);
					}
				}

				return {
					props: {
						body,
						page,
						publications,
					},
				};
			} else {

				return { notFound: true };
			}
		}

	} catch {

		return { notFound: true };
	}
	// #endregion
};
// #endregion

export default GenericPage;
